import {AfterViewInit, Directive, EventEmitter, OnChanges, SimpleChanges} from '@angular/core';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {newGuid, parseOptions} from '../question-helper';

type TileInputType = 'radio' | 'checkbox';
type TileVariant = 'checkbox' | 'radio' | 'switch';

@Directive({
  standalone: false
})
export abstract class QuestionTileBase implements OnChanges, AfterViewInit {

  abstract question: Question;
  abstract readOnly: boolean;
  abstract showLabel: boolean;
  abstract labelText: string;
  abstract showItk: boolean;
  abstract questionAnswer: EventEmitter<any>;
  protected abstract readonly tileVariant: TileVariant;

  items: any[] = [];
  uniqueControlName = `tile-name-${newGuid()}`;
  private itkItems: any[] = [];

  protected constructor(private questionService: GlobalQuestionService) {
  }

  ngAfterViewInit(): void {
    this.questionService.initPopoverCustomize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.question?.currentValue !== changes?.question?.previousValue) {
      this.question = changes?.question?.currentValue || this.question;
      this.setupOptions();
    }
  }

  setupOptions(): void {
    const options = this.question?.options ? parseOptions(this.question.options) : [];

    this.itkItems = this.parseItk(this.question?.itk);
    this.items = (Array.isArray(options) ? options : [])
      .slice()
      .sort((a, b) => this.getOrder(a) - this.getOrder(b))
      .map((option) => ({
        ...option,
        title: option?.title ?? option?.label ?? option?.answer,
        subtitle: option?.subtitle ?? option?.description ?? option?.info_comment,
        showIndicator: this.tileVariant === 'switch'
          ? false
          : option?.showIndicator ?? true,
        reveal: option?.reveal ?? this.isRevealVariant(option),
        switch: this.tileVariant === 'switch',
        imageUrl: option?.imageUrl ?? option?.image_url,
        imageClass: option?.imageClass ?? option?.image_class,
        icons: this.getOptionIcons(option),
        count: option?.count ?? option?.number,
        badge: option?.badge ?? option?.meta,
        itkItems: this.parseItk(option?.itk),
        revealQuestion: this.createRevealQuestion(option)
      }));
  }

  onChange(item: any, event: any): void {
    if (this.isDisabled(item)) {
      return;
    }

    const inputType = this.getInputType(item);
    const answer = item?.answer ?? '';

    if (inputType === 'radio') {
      this.question.answer = answer;
      this.question.answer_id = item?.cent_aq_answer_id;
    } else {
      const checked = !!event?.target?.checked;
      const selectedAnswers = this.getSelectedAnswers().filter(value => value !== answer);

      if (checked) {
        selectedAnswers.push(answer);
      }

      this.question.answer = selectedAnswers.join('|');
    }

    this.questionAnswer.emit(this.question);
  }

  onRevealAnswer(item: any, revealQuestion: any): void {
    item.revealValue = revealQuestion?.answer ?? '';
  }

  isSelected(item: any): boolean {
    return this.getSelectedAnswers().includes(item?.answer);
  }

  getInputType(_item: any): TileInputType {
    return this.tileVariant === 'radio' ? 'radio' : 'checkbox';
  }

  isDisabled(item: any): boolean {
    return this.readOnly || item?.disabled === true || item?.disabled === 1 || item?.disabled === '1';
  }

  isReveal(item: any): boolean {
    return !!item?.reveal;
  }

  hasRightContent(item: any): boolean {
    return !!item?.showPopover
      || !!item?.tooltip
      || !!item?.tooltipContent
      || item?.icons?.length > 0
      || !!item?.imageUrl
      || !!item?.switch
      || this.hasValue(item?.price);
  }

  hasValue(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  hasBadge(item: any): boolean {
    return this.hasValue(item?.badge);
  }

  getBadgeText(item: any): string {
    return typeof item?.badge === 'object'
      ? item.badge?.text ?? item.badge?.label ?? ''
      : item?.badge;
  }

  getBadgeClass(item: any): string {
    const badgeClass = typeof item?.badge === 'object' ? item.badge?.class : item?.badgeClass;
    return badgeClass || 'tag-pill--info';
  }

  getPriceValue(item: any): any {
    return typeof item?.price === 'object'
      ? item.price?.amount ?? item.price?.value
      : item?.price;
  }

  getPriceFrequency(item: any): string {
    return typeof item?.price === 'object'
      ? item.price?.frequency ?? item.price?.frequency_short ?? ''
      : item?.priceFrequency ?? item?.price_frequency ?? '';
  }

  getTooltipContent(item: any): string {
    return item?.tooltipContent
      || (typeof item?.tooltip === 'string' ? item.tooltip : '')
      || '<div class="popover__content">More information</div>';
  }

  getRevealContent(item: any): string {
    return typeof item?.reveal === 'object' ? item.reveal?.content || '' : item?.revealContent || '';
  }

  getItkItems(item: any): any[] {
    return item?.itkItems?.length ? item.itkItems : this.itkItems;
  }

  getItkContent(item: any): string[] {
    return this.getItkItems(item).map(itk => this.getItkText(itk));
  }

  getItkText(item: any): string {
    return typeof item === 'string'
      ? item
      : item?.itk_comment ?? item?.comment ?? item?.text ?? item?.content ?? '';
  }

  getIconName(icon: any): string {
    return typeof icon === 'string' ? icon : icon?.name || icon?.icon || '';
  }

  getIconClass(icon: any, item: any): string {
    return typeof icon === 'object' && icon?.class
      ? icon.class
      : item?.iconClass || 'icon icon--md';
  }

  getLabel(): string {
    return this.question?.question_prompt || this.question?.question_copy || this.question?.email_version || this.labelText;
  }

  private getSelectedAnswers(): string[] {
    return (this.question?.answer || '').split('|').filter(Boolean);
  }

  private getOrder(option: any): number {
    return Number(option?.answer_order ?? option?.order ?? 0);
  }

  private getOptionIcons(option: any): any[] {
    const icons = option?.icons;
    if (!icons) {
      return [];
    }

    return Array.isArray(icons) ? icons : [icons];
  }

  private createRevealQuestion(option: any): any {
    return {
      ...(this.question || {}),
      question_prompt: '',
      email_version: '',
      parameter: null,
      answer: option?.revealValue ?? option?.reveal_answer ?? ''
    };
  }

  private isRevealVariant(option: any): boolean {
    return String(option?.variant || '').toLowerCase().includes('reveal');
  }

  private parseItk(value: any): any[] {
    if (!value) {
      return [];
    }

    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return [value];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (Array.isArray(parsed?.items)) {
      return parsed.items;
    }

    return [parsed];
  }
}
